import React, { useRef, useMemo, memo } from 'react';
import { DPR } from '@/common/device-info';
import { PageContext } from '@/component/RtlLayout';
import { convertStyles } from '@kibt/krn-utils';
// import Image from '@kibt/meta-ui-krn/lib/components/Image';
// import { radar } from '@/common/log';
// import { useScrollBottomLoad } from '@/page/myShoppingCar/views/hooks/usePageInfo';
// import { styles } from '@/common/style';
import SrcStyles from './index.styl';
import { ImageWithBackground } from '@/component/ImageWithBackground';

const Styles = convertStyles(SrcStyles);

type IProps = {
    src: string;
    width?: number;
    height?: number;
    defaultSrc?: string;
    errorType?: string;
    q?: number;
    cropOption?: {
        ratio?: number;
        width?: number;
        height?: number;
    };
    [key: string]: any;
};

// uploadCaches
const notWebPCaches = [];
export const CropImage = memo((props: IProps) => {
    const imgRef = useRef<any>();
    const { src, width, height, defaultSrc, q, errorType = 'default', cropOption, ...restProps } = props;
    const errorSrcMap = {
        avatar: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFQAAABUCAMAAAArteDzAAAAV1BMVEUAAADn5+fk5OTk5OTj4+Pk5OTl5eXf39/k5OTl5eXl5eXk5OTi4uLj4+Pm5ubk5OTk5OTi4uLl5eXk5OT////9/f3n5+f5+fnt7e3y8vL29vbq6urv7+9GZ+CAAAAAE3RSTlMAIO+/34CvEM9gn3CgQG+QMFBP9mL7OAAAAqdJREFUWMOtmQmOozAQRb0BAZKe9HzvcP9zTtKRJlJjV4HJO8DTt8uWi0KwyLGbtDF4oIzuu++7OMfw1StsUP0o240aVXST93ZVoJnkUSURslErexC0aTuF/XT7Yl5wCLMj7JfCQdTMOf+AoG0LBo0m9EBuZyMXWXUaELSVa7jgBJfyDmicQhePPE7yp3A+cZp5UySF06jfxTL4ABd2Q09fLQmaJQVnrQtpObABV1AkZ//jIij6nUGXl/Kt9SC47Qqa7IudYfWeoMkWiFRUPmi0JRxRr4kN6p0tW1FFDT/SEVWyrZC4y6rrQW0Nx5RKskEP1mqgV+/q0kyvvydWX8fRt0qhxmIJiPoLcacOKYFHFSm+G6XE+R9F9/mkHVGn1j3FJDSaqh8IqSYfp9B0TmGEQp2VqBMpBYF3xOoJCCkVdWGlx3c1gZMe34AARmqOW50HidpIeWsGg9kcfu5BdeuOTvUKFv9uUUICT7+zNVtiehD9zjZtxMf5KyQ+zp14TlpRxMNHwbaTMz7M+JAOYFjimnLOIYSc0xoXMEjmoyym4OwGuk3XQtTX71OwBCH66uofDApb1reR8BZXX2t6k7O7cLHW9Irb75SEktXK4vezD/YQeRN0G3Vx9iDOl4K+bxXT6vDWqfQNvVh7yioLH7ze2SZCeZZ0wZNsG4l4YkpDBG9bCcUhgpj5KnE98FwcyKV2aawM5zRVJ77+ujbs8q7ZeRmqYznvGp1GEgNEHxpK/8gpqVFnQ7USPep8Xa3oDi19fQ3kSGYFnw8tXc2CRZr9YV18z2MZup0PikuA6sRO5BXw0fFK9JuYjBYx1415AaBvvGmrfXgLeUOKANS0VfLIUePJknII7scWQl4jnugv4mhy3vJPLsbIc//uem1+3MboqRv52vwD7lCiHpaoriAAAAAASUVORK5CYII=',
        default:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAYAAABV7bNHAAAAAXNSR0IArs4c6QAAAMZJREFUeF7t1LERwCAQxEDTf41fC1SACZQuOYlm/9bM7M+7FlgC/esQ6HE9AgnUBpYggghqBQhq/WwQQQS1AgS1fjaIIIJaAYJaPxtEEEGtAEGtnw0iiKBWgKDWzwYRRFArQFDrZ4MIIqgVIKj1s0EEEdQKENT62SCCCGoFCGr9bBBBBLUCBLV+NogggloBglo/G0QQQa0AQa2fDSKIoFaAoNbPBhFEUCtAUOtngwgiqBUgqPWzQQQR1AoQ1PrZIIIIagUevw9nGgKI/uJcCAAAAABJRU5ErkJggg==',
    };

    // 尺寸处理
    const getImageSize = useMemo(() => {
        // 业务传入宽高，根据 DPR 扩大图
        if (width && height) {
            return {
                cropWidth: width * DPR,
                cropHeight: height * DPR,
            };
        }
        return {
            cropWidth: 0,
            cropHeight: 0,
        };
        // todo 业务没有传入宽高
    }, [width, height]);

    // 进行cdn裁剪
    const getClipImageUrl = useMemo(() => {
        const originUrl = src;
        const { cropWidth, cropHeight } = getImageSize;

        const cdns = ['sign-static.dreamriverclub.com', 's1.kwai.net', 'media-cloud-api.staging.kuaishou.com'];
        const isInCdns = typeof originUrl === 'string' && !!cdns.find((item) => originUrl.split('?')[0].includes(item));

        // 宽和高任意一项为0，或者没命中优化实验或者命中ab，但是没有返回约定格式的图片，直接返回原始url
        // 约定格式的图片：指的是后端返回的可裁剪的图片，一定是webp格式的
        if (!cropWidth || !cropHeight || !isInCdns || !originUrl?.toLowerCase()?.includes('.webp')) {
            if (!notWebPCaches.includes(originUrl)) {
                notWebPCaches.push(originUrl);
            }
            return originUrl;
        }

        // 去掉格式后缀的url，方便进行拼接
        const handledUrl = originUrl.substring(0, originUrl.lastIndexOf('.'));

        // 拼接的裁剪参数
        let croppedParams = '';
        if (q) {
            croppedParams = `:${cropWidth}x${cropHeight}:q${q}`;
        } else {
            croppedParams = `:${cropWidth}x${cropHeight}`;
        }

        // 若做了 CDN 裁剪，则无需组件内部裁剪
        // if (cropOption?.ratio) {
        //     delete cropOption.ratio
        // }

        return `${handledUrl}${croppedParams}.webp`;
        // if (isSupportWebP) {
        //     return `${handledUrl}${croppedParams}.webp`;
        // }
        // 不支持webp
        // return `${handledUrl}${croppedParams}.jpeg`;
    }, [src, q, getImageSize, cropOption]);

    const imgUrl = useMemo(() => {
        return getClipImageUrl || errorSrcMap[errorType] || errorSrcMap.default;
    }, [getClipImageUrl, errorType, errorSrcMap]);

    return (
        <PageContext.Provider value={{ Styles }}>
            <ImageWithBackground
                {...restProps}
                source={{
                    uri: imgUrl,
                }}
                cropOption={cropOption}
                ref={imgRef}
            />
        </PageContext.Provider>
    );
});
export default CropImage;
